import { z } from "zod";
import {
  MAX_PRICE_CENTS,
  MAX_SERVICE_ORDER_ITEMS,
  MAX_STOCK_QTY_Q4,
  SERVICE_ORDER_ITEM_KINDS,
  isIsoDate,
  type EffectState,
  type ServiceOrderItemKind,
} from "@cx/domain";
import { listQuery } from "./common";
import { blank, text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";
import type { FiscalDocumentStatus } from "./fiscal-documents";
import type { SalesOrderStatusRef } from "./sales-orders";
import type { ServiceInvoiceStatus } from "./service-invoices";
import type { StatusView } from "./statuses";

// ===== Ordem de serviço (T2.9, RF-SER-03) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const cents = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(0, "Valor não pode ser negativo")
    .max(MAX_PRICE_CENTS, "Valor acima do limite");
const optDate = blank.pipe(businessDate.nullable());
/** Hora "HH:MM" (00:00 a 23:59) ou vazio. */
const optTime = blank.pipe(
  z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a hora no formato HH:MM")
    .nullable(),
);

/**
 * Item da OS. Serviço: descrição obrigatória, código opcional, horas (q4) × preço por hora, Orçar e OK. Peça: produto
 * cadastrado obrigatório (descrição, código e unidade vêm do cadastro); preço vazio = preço de venda do produto.
 */
export const serviceOrderItemInput = z
  .object({
    kind: z.enum(SERVICE_ORDER_ITEM_KINDS, "Tipo de item inválido"),
    productId: nullish(ulidParam),
    description: text(1000),
    code: text(60),
    qtyQ4: z
      .number("Informe a quantidade")
      .int("Quantidade inválida")
      .min(1, "A quantidade deve ser maior que zero")
      .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite"),
    unitPriceCents: nullish(cents("o preço")),
    toQuote: z.boolean().default(false),
    done: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    if (v.kind === "part") {
      if (v.productId === null)
        ctx.addIssue({ code: "custom", path: ["productId"], message: "Escolha o produto da peça" });
      return;
    }
    if (!v.description)
      ctx.addIssue({ code: "custom", path: ["description"], message: "Descreva o serviço" });
    if (v.unitPriceCents === null)
      ctx.addIssue({ code: "custom", path: ["unitPriceCents"], message: "Informe o preço do serviço" });
  });
export type ServiceOrderItemInput = z.output<typeof serviceOrderItemInput>;

export const serviceOrderInput = z
  .object({
    contactId: ulidParam,
    sellerId: nullish(ulidParam),
    storeId: nullish(ulidParam),
    technician: text(120),
    equipment: text(200),
    serialNumber: text(60),
    problem: text(5000),
    receptionNotes: text(5000),
    internalNotes: text(5000),
    entryDate: businessDate,
    expectedDate: optDate,
    finishDate: optDate,
    exitDate: optDate,
    startTime: optTime,
    endTime: optTime,
    items: z
      .array(serviceOrderItemInput, "Informe os itens")
      .max(MAX_SERVICE_ORDER_ITEMS, `No máximo ${MAX_SERVICE_ORDER_ITEMS} itens`),
    freightCents: cents("o frete").default(0),
    discountCents: cents("o desconto").default(0),
    /** Orçar: a OS é um orçamento (valores a aprovar); Orçado: o orçamento foi feito. */
    quoteMode: z.boolean().default(false),
    quoted: z.boolean().default(false),
    warranty: text(120),
    warrantyInfo: text(5000),
    serviceNotes: text(5000),
    technicianInfo: text(5000),
    paymentTermId: nullish(ulidParam),
    paymentMethodId: nullish(ulidParam),
    financialCategoryId: nullish(ulidParam),
    warehouseId: nullish(ulidParam),
  })
  .superRefine((v, ctx) => {
    const after = (k: "expectedDate" | "finishDate" | "exitDate", label: string) => {
      const d = v[k];
      if (d && isIsoDate(d) && isIsoDate(v.entryDate) && d < v.entryDate)
        ctx.addIssue({ code: "custom", path: [k], message: `${label} não pode ser antes da entrada` });
    };
    after("expectedDate", "A data prevista");
    after("finishDate", "A data de término");
    after("exitDate", "A data de saída");
  });
export type ServiceOrderInput = z.output<typeof serviceOrderInput>;
export type ServiceOrderFormValues = z.input<typeof serviceOrderInput>;

export const serviceOrderListQuery = listQuery.extend({
  statusId: ulidParam.optional(),
  from: businessDate.optional(),
  to: businessDate.optional(),
});
export type ServiceOrderListQuery = z.output<typeof serviceOrderListQuery>;

/** Mudança de situação: a situação que a tela mostrava é obrigatória (409 `status_changed`). */
export const serviceOrderStatusInput = z.object({ toStatusId: ulidParam, expectedFromStatusId: ulidParam });
export type ServiceOrderStatusInput = z.output<typeof serviceOrderStatusInput>;

/** Notas da OS finalizada: NFS-e dos serviços e/ou NF-e das peças (natureza vazia = a do cliente ou a padrão). */
export const serviceOrderInvoicesInput = z
  .object({
    nfse: z.boolean().default(false),
    nfe: z.boolean().default(false),
    taxOperationId: nullish(ulidParam),
  })
  .refine((v) => v.nfse || v.nfe, { message: "Escolha ao menos uma nota", path: ["nfse"] });
export type ServiceOrderInvoicesInput = z.output<typeof serviceOrderInvoicesInput>;

/** Finalizar: situação "Finalizada" (baixa as peças e lança as contas) e, se pedido, as notas. */
export const serviceOrderFinishInput = z.object({
  expectedFromStatusId: ulidParam,
  /** Data de término (base das parcelas); vazio = a gravada na OS ou hoje. */
  finishDate: optDate,
  nfse: z.boolean().default(false),
  nfe: z.boolean().default(false),
  taxOperationId: nullish(ulidParam),
});
export type ServiceOrderFinishInput = z.output<typeof serviceOrderFinishInput>;

export const serviceOrderPdfQuery = z.object({
  kind: z.enum(["os", "receipt"]).default("os"),
});

// ---------- respostas ----------

type Ref = { id: string; name: string } | null;

export type ServiceOrderItemView = {
  position: number;
  kind: ServiceOrderItemKind;
  productId: string | null;
  description: string;
  code: string | null;
  unit: string | null;
  qtyQ4: number;
  unitPriceCents: number;
  totalCents: number;
  toQuote: boolean;
  done: boolean;
};

export type ServiceOrderTotalsView = {
  servicesTotalCents: number;
  partsTotalCents: number;
  freightCents: number;
  discountCents: number;
  totalCents: number;
  hoursSumQ4: number;
};

export type ServiceOrderInvoiceRef = {
  nfse: { id: string; status: ServiceInvoiceStatus; number: string | null } | null;
  nfe: { id: string; status: FiscalDocumentStatus; number: number | null } | null;
};

export type ServiceOrderView = ServiceOrderTotalsView & {
  id: string;
  number: number;
  contact: { id: string; name: string; document: string | null };
  seller: Ref;
  store: Ref;
  technician: string | null;
  equipment: string | null;
  serialNumber: string | null;
  problem: string | null;
  receptionNotes: string | null;
  internalNotes: string | null;
  entryDate: string;
  expectedDate: string | null;
  finishDate: string | null;
  exitDate: string | null;
  startTime: string | null;
  endTime: string | null;
  items: ServiceOrderItemView[];
  quoteMode: boolean;
  quoted: boolean;
  warranty: string | null;
  warrantyInfo: string | null;
  serviceNotes: string | null;
  technicianInfo: string | null;
  paymentTerm: Ref;
  paymentMethod: Ref;
  financialCategory: Ref;
  warehouse: Ref;
  status: SalesOrderStatusRef;
  nextStatuses: SalesOrderStatusRef[];
  effects: EffectState;
  /** Motivo da trava de edição; null = pode editar. */
  lockedReason: string | null;
  /** Pode emitir notas (finalizada, exceto sem conserto). */
  invoiceable: boolean;
  /** Última NFS-e e NF-e da OS. */
  invoices: ServiceOrderInvoiceRef;
  /** Contas a receber lançadas pela OS. */
  titles: { id: string; dueDate: string; amountCents: number; paidCents: number; status: string }[];
  createdAt: string;
  updatedAt: string;
};

export type ServiceOrderListItem = {
  id: string;
  number: number;
  entryDate: string;
  contactName: string;
  equipment: string | null;
  technician: string | null;
  totalCents: number;
  status: SalesOrderStatusRef;
};
export type ServiceOrderListResult = {
  data: ServiceOrderListItem[];
  nextCursor: string | null;
  summary: { count: number; totalCents: number | null };
};

export type ServiceOrderFormOptions = {
  sellers: { id: string; name: string }[];
  sellerLocked: boolean;
  stores: { id: string; name: string }[];
  paymentTerms: { id: string; name: string }[];
  paymentMethods: { id: string; name: string }[];
  financialCategories: { id: string; name: string }[];
  warehouses: { id: string; name: string; isDefault: boolean }[];
  taxOperations: { id: string; name: string; isDefault: boolean }[];
  statuses: StatusView[];
};

/** Resultado de uma nota pedida: id (null = não criada), situação e o problema que a deixou em rascunho. */
export type ServiceOrderInvoiceOutcome = {
  id: string | null;
  status: string | null;
  error: string | null;
} | null;
export type ServiceOrderInvoicesResult = {
  nfse: ServiceOrderInvoiceOutcome;
  nfe: ServiceOrderInvoiceOutcome;
};
export type ServiceOrderFinishResult = ServiceOrderInvoicesResult & { changed: boolean };

export type ServiceOrderPrintData = {
  order: ServiceOrderView;
  company: {
    name: string;
    tradeName: string | null;
    document: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
  };
  contact: {
    name: string;
    document: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
  };
};
export type ServiceOrderPdfLink = { url: string; fileName: string };
